// The game frame (spec/dashboard.md section 3.3): the rail, the navigation panel, the top bar, the content, the dock;
// the branch workspace when a branch is in the URL; the shell banners (the protected one offers New branch… and moves the
// drafts there, section 4.3 step 6); the keyboard shortcuts and the command palette.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { ops } from '../api/operations';
import type { EnumDefinition, LocalizationSettings, LocalizationString, Product, Template } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { productLabelOf } from '../content/ContentContext';
import { searchHitLabel, type FormatContext } from '../content/format';
import { Banners, type BannerMessage } from '../ui/Banner';
import { ChangesPanel } from '../ui/ChangesPanel';
import { CommandPalette, type Command } from '../ui/CommandPalette';
import { KeyboardShortcuts } from '../ui/KeyboardShortcuts';
import { LogPanel } from '../ui/LogPanel';
import { useExtensionHost } from '../extensions/ExtensionsContext';
import { ExtensionsMenu } from '../extensions/ExtensionsMenu';
import { extensionPreSaveHook } from '../extensions/preSaveHook';
import { NewBranchDialog } from '../pages/history/BranchDialogs';
import { queryCache } from '../workspace/cache';
import { HeadWatcher } from '../workspace/head';
import { WorkspaceProvider, useWorkspaceVersion, workspaceFor } from '../workspace/react';
import type { Draft } from '../workspace/Workspace';
import { areaOf, areas, itemHref } from './areas';
import { catalogLabels, crumbsFor } from './crumbs';
import { Dock, useDock } from './Dock';
import { defaultEnv, rememberEnv } from './envMemory';
import { GameProvider, useGame } from './GameContext';
import { NavPanel } from './NavPanel';
import { Rail } from './Rail';
import { controlItems, liveOpsItems, routes } from './routes';
import { TopBar, type Crumb } from './TopBar';
import { lookupEnvironment, looksLikePlayerId, readableEnvironments, searchIds } from './idSearch';
import { useSaveAll } from './useSaveAll';

const PANEL_KEY = 'hue.panel';

export function GameLayout() {
  return (
    <GameProvider>
      <GameFrame />
    </GameProvider>
  );
}

function GameFrame() {
  const { org, game, branches, environments, can, offsets } = useGame();
  const { branch = null, env = null } = useParams<{ branch?: string; env?: string }>();
  const { client } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // an environment page (platforms, settings — section 9.11–9.12) stages into a workspace of its own, keyed by the environment
  const workspaceKey = branch ?? (env ? `env:${env}` : null);
  const workspace = useMemo(() => (workspaceKey ? workspaceFor(game.id, workspaceKey) : null), [game.id, workspaceKey]);
  const workspaceVersion = useWorkspaceVersion(workspace);
  const dock = useDock();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(() => {
    try {
      return localStorage.getItem(PANEL_KEY) === 'collapsed';
    } catch {
      return false;
    }
  });
  const setPanel = (collapsed: boolean) => {
    setPanelCollapsed(collapsed);
    try {
      if (collapsed) localStorage.setItem(PANEL_KEY, 'collapsed');
      else localStorage.removeItem(PANEL_KEY);
    } catch {
      // the choice then lasts for the page
    }
  };
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  /** the protected banner's New branch… dialog: 'move' carries this branch's drafts along (section 4.3 step 6) */
  const [newBranch, setNewBranch] = useState<'move' | 'new' | null>(null);
  const { host } = useExtensionHost(workspaceKey);
  const hooks = useMemo(() => (host ? [extensionPreSaveHook(host)] : []), [host]);
  const { saveAll, dryRun, discardAll, saving, reasonDialog } = useSaveAll(workspace, hooks, { onFailure: () => dock.show('changes') });
  // the permission Save All needs: content_write on a branch; on an environment page manage_environment for its records and
  // players_write for player keys (sections 9.12, 11.4) — the Support role holds the latter without any content permission
  const writePermission = useMemo(() => {
    if (branch || !env) return can('content_write') ? null : 'content_write';
    const kinds = new Set((workspace?.all ?? []).map((d) => d.kind));
    if (kinds.has('player-key') && !can('players_write', env)) return 'players_write';
    if (Array.from(kinds).some((k) => k !== 'player-key') && !can('manage_environment', env)) return 'manage_environment';
    if (kinds.size === 0 && !can('manage_environment', env) && !can('players_write', env)) return 'manage_environment';
    return null;
  }, [branch, env, workspace, can, workspaceVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const canWrite = writePermission === null;

  // the head watcher of the branch on screen: the event stream, the poll as its fallback (section 4.6)
  const branchId = branches.find((x) => x.name === branch)?.id ?? null;
  useEffect(() => {
    if (!workspace || !branch) return;
    const watcher = new HeadWatcher({
      client,
      org,
      game: game.slug,
      branch,
      branchId,
      workspace,
      onChange: () => queryCache.invalidate(`branch:${game.id}:${branch}`),
    });
    const b = branches.find((x) => x.name === branch);
    if (b) {
      workspace.setProtected(b.isProtected);
      if (b.head && workspace.head === null) workspace.setHead(b.head, { remote: true });
    }
    watcher.start();
    return () => watcher.stop();
  }, [workspace, branch, branchId, client, org, game.slug, game.id, branches]);

  const openDraft = useCallback(
    (d: Draft) => {
      if (!branch) return;
      const template = d.template ?? '';
      const params = new URLSearchParams(location.search);
      params.set('doc', d.id);
      navigate(`${routes.documents(org, game.slug, branch, template)}?${params.toString()}`);
    },
    [branch, location.search, navigate, org, game.slug],
  );

  // where on the game we are: the page segment after the branch, the environment or the game, and its area
  const scope: 'branch' | 'env' | 'game' = branch ? 'branch' : env ? 'env' : 'game';
  const pageSegment = (branch ? location.pathname.split(`/branches/${encodeURIComponent(branch)}/`)[1] : env ? location.pathname.split(`/envs/${encodeURIComponent(env)}/`)[1] : location.pathname.split(`/games/${encodeURIComponent(game.slug)}/`)[1]) ?? '';
  const area = areaOf(pageSegment.split('?')[0]!, scope);
  const defaultBranch = branch ?? branches.find((x) => x.isDefault)?.name ?? branches[0]?.name ?? 'main';
  // the environment-scoped rail links lead to the environment last worked in on this game (shell/envMemory.ts)
  useEffect(() => {
    if (env && environments.some((e) => e.slug === env)) rememberEnv(game.id, env);
  }, [env, environments, game.id]);
  const railEnv = env ?? defaultEnv(game.id, environments);
  const railAreas = areas.map((a) => ({ key: a.key, label: a.label, icon: a.icon, active: a.key === area, to: itemHref(a.items[0]!, { org, game: game.slug, branch: defaultBranch, env: railEnv }) }));

  // the page crumbs are page titles (shell/crumbs.ts), the template and enum names their display names from the catalog
  const crumbs: Crumb[] = [{ label: org, to: routes.games(org) }, { label: game.name, to: routes.game(org, game.slug) }];
  if (branch) crumbs.push({ label: branch, to: routes.templates(org, game.slug, branch) });
  if (env) crumbs.push({ label: env });
  crumbs.push(...crumbsFor(pageSegment, { org, game: game.slug, branch, env, ...catalogLabels(game.id, branch) }));

  const banners: BannerMessage[] = [];
  if (workspace?.restoredCount && !dismissed.has('restored')) {
    banners.push({ id: 'restored', tone: 'info', body: `${workspace.restoredCount} unsaved change${workspace.restoredCount === 1 ? '' : 's'} restored from this tab.`, action: { label: 'Show', onClick: () => dock.show('changes') }, onDismiss: () => setDismissed((s) => new Set(s).add('restored')) });
  }
  // commits of others that landed on this branch since the page last refreshed (section 4.6)
  const remoteCount = branch ? (workspace?.remoteChangeCount ?? 0) : 0;
  if (workspace && remoteCount > 0) {
    banners.push({
      id: 'remote-changes',
      tone: 'info',
      body: `${remoteCount} change${remoteCount === 1 ? '' : 's'} landed on this branch.`,
      action: {
        label: 'Refresh',
        onClick: () => {
          workspace.clearRemoteChanges();
          queryCache.invalidate();
        },
      },
      onDismiss: () => workspace.clearRemoteChanges(),
    });
  }
  // the way out of a protected branch (section 4.3 step 6): its drafts move to a new branch forked from its head
  if (workspace?.isProtected && !dismissed.has('protected')) {
    const stranded = branch !== null && workspace.isDirty;
    banners.push({
      id: 'protected',
      tone: 'warning',
      body: `Branch ${branch} is protected: changes land through merge requests.`,
      action: branch ? { label: stranded ? 'Move my changes to a new branch…' : 'New branch…', onClick: () => setNewBranch(stranded ? 'move' : 'new') } : undefined,
      onDismiss: () => setDismissed((s) => new Set(s).add('protected')),
    });
  }

  const commands = useMemo<Command[]>(() => {
    const list: Command[] = [];
    if (branch) {
      for (const item of [...controlItems, ...liveOpsItems]) {
        if (item.scope === 'branch') list.push({ id: `page:${item.key}`, label: item.label, group: 'Pages', run: () => navigate(routes.branch(org, game.slug, branch, item.page)) });
      }
    }
    for (const b of branches) list.push({ id: `branch:${b.name}`, label: b.name, group: 'Branches', hint: b.isProtected ? 'protected' : undefined, run: () => navigate(routes.templates(org, game.slug, b.name)) });
    list.push({ id: 'page:game', label: 'Game home', group: 'Pages', run: () => navigate(routes.game(org, game.slug)) });
    // the support pages on every page (section 11): Players and Orders of each environment the caller reads
    for (const e of readableEnvironments(environments, can)) {
      list.push({ id: `page:players:${e.slug}`, label: `Players · ${e.slug}`, group: 'Pages', hint: e.name, run: () => navigate(routes.players(org, game.slug, e.slug)) });
      list.push({ id: `page:orders:${e.slug}`, label: `Orders · ${e.slug}`, group: 'Pages', hint: e.name, run: () => navigate(routes.orders(org, game.slug, e.slug)) });
    }
    list.push({ id: 'action:save', label: 'Save all', group: 'Actions', hint: 'Ctrl+S', run: () => void saveAll() });
    list.push({ id: 'action:changes', label: 'Open the Changes panel', group: 'Actions', run: () => dock.show('changes') });
    return list;
  }, [branch, branches, navigate, org, game.slug, saveAll, dock, environments, can]);

  // the branch content results (section 6.6): five per group, then "Show all results…"
  const searchContent = useCallback(
    async (text: string, signal: AbortSignal): Promise<Command[]> => {
      if (!branch) return [];
      const path = { org, game: game.slug, branch };
      // five per group (section 6.6); a group whose endpoint fails is left out, the others still answer
      const settle = <T,>(p: Promise<{ items: T[] }>) => p.then((r) => r.items.slice(0, 5)).catch((e: unknown) => {
        if ((e as Error).name === 'AbortError') throw e;
        return [] as T[];
      });
      const query = { search: text, limit: 5 };
      const [docs, templates, strings, assets, products] = await Promise.all([
        settle(client.data(ops.listDocuments, { path, query, signal })),
        settle(client.data(ops.listTemplates, { path, query, signal })),
        settle(client.data(ops.listLocalizationStrings, { path, query, signal })),
        settle(client.data(ops.listAssets, { path, query, signal })),
        settle(client.data(ops.listProducts, { path, query, signal })),
      ]);
      const page = (p: string, key: string, id: string) => `${routes.branch(org, game.slug, branch, p)}?${key}=${encodeURIComponent(id)}`;
      // document hits are labelled by the display format (section 6.5) from the branch catalog the pages already loaded
      // into the query cache; before it is there, the label is the template's short name and the id
      const cacheKey = (suffix: string) => `branch:${game.id}:${branch}:${suffix}`;
      const catalogTemplates = queryCache.peek<Template[]>(cacheKey('templates:all'));
      const catalogStrings = new Map((queryCache.peek<LocalizationString[]>(cacheKey('strings:all')) ?? []).map((s) => [s.id, s]));
      const catalogProducts = new Map<string, Product>();
      for (const p of queryCache.peek<Product[]>(cacheKey('products:all')) ?? []) {
        catalogProducts.set(p.id, p);
        if (p.itemId) catalogProducts.set(p.itemId, p);
      }
      const defaultLanguage = queryCache.peek<LocalizationSettings>(cacheKey('localization-settings'))?.defaultLanguage ?? 'en';
      const hits = new Map(docs.map((d) => [d.id, d]));
      const format: FormatContext = {
        enums: new Map((queryCache.peek<EnumDefinition[]>(cacheKey('enums')) ?? []).map((e) => [e.name, e])),
        templates: catalogTemplates ? new Map(catalogTemplates.map((t) => [t.name, t])) : undefined,
        offsets,
        resolve: (id) => hits.get(id),
        stringText: (id) => {
          const s = catalogStrings.get(id);
          if (!s) return undefined;
          const values = (s.values ?? {}) as Record<string, string>;
          return values[defaultLanguage] ?? Object.values(values)[0] ?? s.key;
        },
        productLabel: (productId) => {
          const p = catalogProducts.get(productId);
          return p ? productLabelOf(p) : undefined;
        },
      };
      const templateLabel = (name: string) => format.templates?.get(name)?.displayName ?? name;
      return [
        ...templates.map((t) => ({ id: `tpl:${t.id}`, label: t.displayName ?? t.name, group: 'Templates', hint: t.name, run: () => navigate(routes.documents(org, game.slug, branch, t.name)) })),
        ...docs.map((d) => ({ id: `doc:${d.id}`, label: searchHitLabel(d, format), group: 'Documents', hint: templateLabel(d.template), run: () => navigate(`${routes.documents(org, game.slug, branch, d.template)}?doc=${d.id}`) })),
        ...strings.map((s) => ({ id: `str:${s.id}`, label: s.fullKey, group: 'Localization strings', hint: Object.values(s.values ?? {})[0], run: () => navigate(page('localization/strings', 'string', s.id)) })),
        ...assets.map((a) => ({ id: `asset:${a.id}`, label: a.name, group: 'Assets', hint: a.kind, run: () => navigate(page('assets/files', 'asset', a.id)) })),
        ...products.map((p) => ({ id: `product:${p.id}`, label: p.name, group: 'Products', hint: p.itemId, run: () => navigate(page('products', 'product', p.id)) })),
        { id: `search:${text}`, label: `Show all results for '${text}'…`, group: 'Search', run: () => navigate(routes.search(org, game.slug, branch, text)) },
      ];
    },
    [branch, client, org, game.slug, game.id, navigate, offsets],
  );

  // the id lookups (shell/idSearch.ts): a player or payment id typed in the palette, in every environment the caller reads,
  // listed before the content results on every page; an id-shaped text nothing matched offers the players page's lookup
  const search = useCallback(
    async (text: string, signal: AbortSignal): Promise<Command[]> => {
      const idCtx = { client, org, game: game.slug, environments, can, navigate: (to: string) => void navigate(to) };
      const [ids, content] = await Promise.all([searchIds(text, idCtx, signal), searchContent(text, signal)]);
      const lookupEnv = ids.length === 0 && looksLikePlayerId(text) ? lookupEnvironment(environments, can) : null;
      const fallback: Command[] = lookupEnv
        ? [{ id: `find-player:${text}`, label: `Find player “${text}” in ${lookupEnv.name}…`, group: 'Players', run: () => void navigate(`${routes.players(org, game.slug, lookupEnv.slug)}?value=${encodeURIComponent(text)}`) }]
        : [];
      return [...ids, ...fallback, ...content];
    },
    [client, org, game.slug, environments, can, navigate, searchContent],
  );

  const frame = (
    <div className={`shell ${panelCollapsed ? 'panel-collapsed' : ''}`}>
      <Rail areas={railAreas} />
      <NavPanel area={area} branch={branch} env={env} head={workspace?.head ?? null} onCollapse={() => setPanel(true)} />
      <TopBar crumbs={crumbs} extensions={<ExtensionsMenu host={host} onOpenLogs={() => dock.toggle('logs')} />} workspace={workspace} onSaveAll={() => void saveAll()} onDiscardAll={() => void discardAll()} onUndo={() => workspace?.undo()} onRedo={() => workspace?.redo()} onOpenChanges={() => dock.show('changes')} onPalette={() => setPaletteOpen(true)} saving={saving} canWrite={canWrite} writePermission={writePermission ?? 'content_write'} zoneCtx={{ offsets }} panelCollapsed={panelCollapsed} onShowPanel={() => setPanel(false)} />
      <main className="content">
        <Banners messages={banners} />
        <Outlet />
      </main>
      <Dock dock={dock} changes={workspace ? <ChangesPanel workspace={workspace} onOpen={openDraft} onSaveAll={() => void saveAll()} onDryRun={() => void dryRun()} onDiscardAll={() => void discardAll()} saving={saving} canWrite={canWrite} writePermission={writePermission ?? 'content_write'} /> : <p className="muted">Pick a branch to see its changes.</p>} logs={<LogPanel entries={host?.logs ?? []} onClear={(source) => { host?.clearLogs(source); }} />} />
      <KeyboardShortcuts onSave={() => void saveAll()} onUndo={() => workspace?.undo()} onRedo={() => workspace?.redo()} onPalette={() => setPaletteOpen(true)} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} commands={commands} search={search} />
      {reasonDialog}
      {branch && workspace && (
        <NewBranchDialog open={newBranch !== null} onClose={() => setNewBranch(null)} branch={branch} fromSha={null} moveFrom={newBranch === 'move' ? workspace : undefined} samePage />
      )}
    </div>
  );
  return workspace && workspaceKey ? (
    <WorkspaceProvider gameId={game.id} branch={workspaceKey}>
      {frame}
    </WorkspaceProvider>
  ) : (
    frame
  );
}
